import Head from 'next/head';
import { useRef, useState } from 'react';

import img2d from '../assets/images/features/2d.gif';
import assignImg from '../assets/images/features/assign.gif';
import dragDropImg from '../assets/images/features/dragDrop.gif';
import exportImg from '../assets/images/features/export.gif';
import processImg from '../assets/images/features/process.gif';
import rangesImg from '../assets/images/features/ranges.gif';
import superimposeImg from '../assets/images/features/superimpose.gif';
import FeatureItem from '../components/FeatureItem';
import { SimpleSideMenu } from '../components/SideMenu';
import Section from '../components/marketing/Section';

const features = [
  {
    title: 'Drag / Drop',
    id: 'drag-and-drop',
    description: 'Drag / Drop files or folder from your Desktop',
    content:
      'NMRium is a versatile software platform that facilitates the analysis of nuclear magnetic resonance (NMR) spectroscopy data. It supports multiple file formats, including JCAMP-DX, Bruker folder, and 1D spectra of JEOL and Varian. The software offers a user-friendly interface, allowing users to easily load their NMR spectra by simply dragging and dropping the corresponding files into the browser. Once the spectra are loaded, NMRium automatically groups them based on their corresponding nucleus. This feature streamlines the data analysis process and enables efficient data interpretation.',
    media: dragDropImg,
  },
  {
    title: '1D spectra',
    id: '1d-spectra',
    description: 'Process 1D spectra directly in the browser',
    content:
      'The 1D free induction decay (FID) spectrum can be directly processed using web-based tools. The processing tasks that can be accomplished include line broadening, zero filling, automatic or manual phase correction, and baseline correction. In addition, the previsualization feature facilitates the processing procedure, providing a simplified approach to data analysis.',
    media: processImg,
  },
  {
    title: 'Smart peak picking',
    id: 'smart-peak-picking',
    description: 'Smart peak picking',
    content:
      'NMRium introduces a novel concept of "ranges" in nuclear magnetic resonance (NMR) spectroscopy, which simplifies the analysis of complex spectra. A "range" refers to the integration zone that may encompass one or more "signals" corresponding to the analysed multiplets. The software facilitates automatic "range" picking with a single click, thereby streamlining the data processing pipeline. Additionally, the "ranges" can be exported in the desired publication format, enhancing the reproducibility and transparency of the results.',
    media: rangesImg,
  },
  {
    title: '2D spectra',
    id: '2d-spectra',
    description: 'Display 2D spectra with 1D traces',
    content:
      'NMRium not only facilitates the visualization of one-dimensional (1D) NMR spectra but also provides the capability to display two-dimensional (2D) spectra. Additionally, users can utilize the zoom functionality to magnify specific regions of interest, while simultaneously including 1D traces along the sides of the spectrum.',
    media: img2d,
  },
  {
    title: 'Assign molecules',
    id: 'assign-molecules',
    description: 'Assign simple molecules',
    content:
      'Assigning atoms to specific ranges in NMR spectra can be accomplished easily through simple mouse clicks. NMRium is capable of recognizing and highlighting equivalent atoms based on their diastereotopicity. This feature simplifies the interpretation of complex spectra and allows for more efficient and accurate analysis of molecular structures.',
    media: assignImg,
  },
  {
    title: 'Multiple spectra',
    id: 'multiple-spectra',
    description: 'Superimpose many spectra',
    content:
      'NMRium offers the versatility to analyze multiple samples simultaneously. Beyond analyzing individual samples, it also allows for convenient batch processing by dragging and dropping a folder containing over 100 1D experiments.',
    media: superimposeImg,
  },
  {
    title: 'Export',
    id: 'export',
    description: 'Export the data as SVG or PNG',
    content:
      'The results of the analysis can be easily integrated into other software applications or exported as either a PNG or SVG file format. This streamlined process allows for efficient and flexible use of the data across various platforms and facilitates seamless collaboration between researchers.',
    media: exportImg,
  },
];

export default function Features() {
  const [selected, setSelected] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  function handleScroll() {
    if (ref.current) {
      const featuresAnchors = ref.current.querySelectorAll('.feature');

      const { top: TOP_OFFSET } = ref.current.getBoundingClientRect();

      if (featuresAnchors) {
        let index = -1;
        while (index < featuresAnchors.length - 1) {
          const featureAnchor = featuresAnchors[index + 1];
          const { bottom } = featureAnchor.getBoundingClientRect();
          if (
            bottom > TOP_OFFSET ||
            ref.current.scrollTop >=
              ref.current.scrollHeight - ref.current.offsetHeight - 2
          ) {
            break;
          }
          index += 1;
        }
        if (
          ref.current.scrollTop >=
          ref.current.scrollHeight - ref.current.offsetHeight - 2
        ) {
          setSelected(featuresAnchors.length - 1);
        } else {
          setSelected(index + 1);
        }
      }
    }
  }

  return (
    <>
      <Head>
        <title>NMRium - Features</title>
        <meta
          name="description"
          content="Get a quick overview of the features provided by NMRium."
        />
      </Head>
      <div className="flex md:h-[calc(100dvh-68px)] flex-col items-center text-white md:pb-0">
        <Section title="Features" />
        <div className="flex flex-row min-h-0 flex-1 max-w-6xl">
          <div
            ref={ref}
            className="thinScrollbar px-10 pb-4 text-justify lg:space-y-16 space-y-8 lg:overflow-y-scroll mb-8 shrink"
            onScroll={handleScroll}
          >
            {features.map((feature) => (
              <FeatureItem key={feature.id} feature={feature} />
            ))}
          </div>
          <SimpleSideMenu items={features} selected={selected} />
        </div>
      </div>
    </>
  );
}
